import { useRef, type WheelEvent } from "react";
import { cn } from "@/lib/utils";
import { styleApplyButtonLabel } from "@/lib/carouselStyleApply";

export type StyleApplyMode = "bg" | "full" | "layout";

export type BgPresetRow = {
  id: string;
  label: string;
  hint?: string;
  swatch?: string;
  flat_bg?: string;
  family?: string;
  style_id?: string;
  visual_pack_id?: string;
};

function previewUrl(id: string) {
  return `/bg-presets/${id}.jpg`;
}

function modeHint(mode: StyleApplyMode): string {
  if (mode === "full") {
    return "Wybierz kafelek, potem Zastosuj. Tło + fonty + rytm na całą serię (PNG się przebudują; bez nowego Gemini).";
  }
  if (mode === "layout") {
    return "Wybierz kafelek, potem Zastosuj layout. Fonty i rozkład; Twoje tło bez zmian.";
  }
  return "Wybierz kafelek, potem Zapisz typ tła. Sam klimat, bez przebudowy PNG.";
}

export default function BgPresetPicker({
  presets,
  value,
  onChange,
  mode = "bg",
  onModeChange,
  disabled = false,
  applyDirty = false,
  applyBusy = false,
  onApply,
}: {
  presets: BgPresetRow[];
  value: string;
  onChange: (id: string) => void;
  mode?: StyleApplyMode;
  onModeChange?: (mode: StyleApplyMode) => void;
  disabled?: boolean;
  /** Gdy true, pokazuje przycisk zatwierdzenia wyboru kafelka (klik sam nic nie zapisuje). */
  applyDirty?: boolean;
  applyBusy?: boolean;
  onApply?: () => void;
}) {
  const scroller = useRef<HTMLDivElement>(null);

  function onWheel(e: WheelEvent<HTMLDivElement>) {
    const el = scroller.current;
    if (!el) return;
    if (Math.abs(e.deltaY) >= Math.abs(e.deltaX)) {
      el.scrollLeft += e.deltaY;
      e.preventDefault();
      e.stopPropagation();
    } else {
      e.stopPropagation();
    }
  }

  if (!presets.length) return null;
  return (
    <div className="w-full min-w-0 max-w-full space-y-1.5">
      <p className="text-xs text-muted-foreground">
        Typ tła. Przewiń galerię w poziomie (pasek pod spodem).
      </p>
      {onModeChange ? (
        <div className="flex flex-wrap items-center gap-1.5">
          <button
            type="button"
            disabled={disabled}
            className={cn(
              "rounded-md border px-2 py-1 text-[11px] transition",
              mode === "bg"
                ? "border-brand bg-brand/10 font-medium text-brand"
                : "border-input bg-card text-foreground hover:border-brand/40",
            )}
            onClick={() => onModeChange("bg")}
            title="Tylko klimat tła Gemini / druk. Style i paczka layoutu bez zmian."
          >
            Tylko tło
          </button>
          <button
            type="button"
            disabled={disabled}
            className={cn(
              "rounded-md border px-2 py-1 text-[11px] transition",
              mode === "full"
                ? "border-brand bg-brand/10 font-medium text-brand"
                : "border-input bg-card text-foreground hover:border-brand/40",
            )}
            onClick={() => onModeChange("full")}
            title="Tło + styl (paleta, font house) + paczka rytmu (przepisy Pillow: strzałki, belki, rozkład)."
          >
            Tło + layout
          </button>
          <button
            type="button"
            disabled={disabled}
            className={cn(
              "rounded-md border px-2 py-1 text-[11px] transition",
              mode === "layout"
                ? "border-brand bg-brand/10 font-medium text-brand"
                : "border-input bg-card text-foreground hover:border-brand/40",
            )}
            onClick={() => onModeChange("layout")}
            title="Fonty i rozkład z kafelka. Nie rusza tła, które już masz (własne / Gemini / kadr)."
          >
            Tylko layout
          </button>
          <span className="text-[10px] text-muted-foreground">{modeHint(mode)}</span>
        </div>
      ) : null}
      <div
        ref={scroller}
        onWheel={onWheel}
        className={cn(
          "w-full min-w-0 max-w-full overflow-x-scroll overflow-y-hidden",
          "rounded-md border border-border bg-muted/30 p-2",
          "[overscroll-behavior:contain]",
        )}
        style={{ WebkitOverflowScrolling: "touch" }}
      >
        <div className="flex w-max flex-nowrap gap-2">
          <button
            type="button"
            disabled={disabled}
            title="Bez presetu: styl wizualny + hint"
            className={cn(
              "flex w-[5.75rem] shrink-0 flex-col overflow-hidden rounded-md border bg-card text-left transition",
              !value ? "border-brand ring-2 ring-brand/30" : "border-input hover:border-brand/40",
            )}
            onClick={() => onChange("")}
          >
            <span className="relative block aspect-[4/5] w-full bg-muted">
              <img
                src="/bg-presets/_auto.jpg"
                alt=""
                className="h-full w-full object-cover"
                loading="lazy"
                onError={(e) => {
                  e.currentTarget.style.display = "none";
                }}
              />
            </span>
            <span
              className={cn(
                "truncate px-1.5 py-1 text-[10px] leading-tight",
                !value ? "bg-brand/10 font-medium text-brand" : "text-muted-foreground",
              )}
            >
              Auto
            </span>
          </button>
          {presets.map((row) => {
            const active = value === row.id;
            return (
              <button
                key={row.id}
                type="button"
                disabled={disabled}
                title={
                  mode === "full" || mode === "layout"
                    ? `${row.hint || row.label}${
                        row.visual_pack_id ? ` · paczka ${row.visual_pack_id}` : ""
                      }`
                    : row.hint || row.label
                }
                className={cn(
                  "flex w-[5.75rem] shrink-0 flex-col overflow-hidden rounded-md border bg-card text-left transition",
                  active ? "border-brand ring-2 ring-brand/30" : "border-input hover:border-brand/40",
                )}
                onClick={() => onChange(row.id)}
              >
                <span
                  className="relative block aspect-[4/5] w-full"
                  style={{ background: row.flat_bg || row.swatch || "#f3efe6" }}
                >
                  <img
                    src={previewUrl(row.id)}
                    alt=""
                    className="h-full w-full object-cover"
                    loading="lazy"
                  />
                  {(row.flat_bg || row.swatch) && (
                    <span
                      className="absolute bottom-1 right-1 h-3 w-3 rounded-sm border border-white/70 shadow-sm"
                      style={{ background: row.flat_bg || row.swatch }}
                      title={row.flat_bg || row.swatch}
                    />
                  )}
                </span>
                <span
                  className={cn(
                    "flex min-h-[2.4rem] flex-col gap-0.5 px-1.5 py-1 text-[10px] leading-tight",
                    active ? "bg-brand/10 font-medium text-brand" : "text-foreground",
                  )}
                >
                  <span className="line-clamp-2">{row.label}</span>
                  {row.flat_bg || row.swatch ? (
                    <span className="truncate font-mono text-[9px] font-normal text-muted-foreground">
                      {(row.flat_bg || row.swatch || "").toLowerCase()}
                    </span>
                  ) : row.family ? (
                    <span className="truncate text-[9px] font-normal uppercase tracking-wide text-muted-foreground">
                      {row.family}
                    </span>
                  ) : null}
                </span>
              </button>
            );
          })}
        </div>
      </div>
      {onApply ? (
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            disabled={disabled || applyBusy || !applyDirty}
            className={cn(
              "rounded-md border px-2.5 py-1.5 text-[11px] font-medium transition",
              applyDirty && !applyBusy
                ? "border-brand bg-brand text-white hover:opacity-90"
                : "border-input bg-muted text-muted-foreground",
            )}
            onClick={() => onApply()}
          >
            {applyBusy ? "Składam serię…" : styleApplyButtonLabel(mode)}
          </button>
          {applyDirty ? (
            <span className="text-[10px] text-muted-foreground">
              Klik kafelka tylko wybiera. Bez tego przycisku seria się nie zmienia.
            </span>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
